import type { ProductDimensionValue } from '@packages/gql/generated/shopping/graphql';
import { Box, CloseIconButton, Modal, Slide, Stack, Typography } from '@packages/shared';
import { useTracking } from '@packages/tracking';
import { useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { ColorPicker32 } from '@packages/themes/icons';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData, graphql } from '@packages/gql/generated/shopping';
import { HorizontalScroll } from '../../HorizontalScroll';
import { ImageGalleryColorDialogItem } from './ImageGalleryColorDialogItem';
import { ImageGalleryCallToActionButton } from '../../ImageGalleryCallToActionButton';

const ColorPickerFragment = graphql(/* GraphQL */ `
  fragment ColorPickerFragment on Product {
    dimensions {
      axisType
      values {
        sku
        variationMainImage
        isSelected
        rgb
        displayText
      }
    }
  }
`);

const messages = defineMessages({
  selectColor: {
    id: 'imagegallery.colorPickerDialog.selectColor',
    defaultMessage: 'Farbe wählen',
  },
});
export interface ImageGalleryColorDialogProps {
  /**
   * Target (COLOR) dimension of the product
   */
  productData: FragmentType<typeof ColorPickerFragment>;
  /**
   *
   * color change handler
   * @param sku - sku for selected color
   */
  onChange?: (sku: string) => void;
}

export const ImageGalleryColorDialog = ({
  productData,
  onChange,
}: ImageGalleryColorDialogProps) => {
  const { dimensions } = getFragmentData(ColorPickerFragment, productData);
  const [openColorDialog, setOpenColorDialog] = useState(false);
  const dispatchGTMEvent = useTracking();
  const { formatMessage } = useIntl();
  const dispatch = useTracking();
  const onSelect = (value: ProductDimensionValue) => {
    setTimeout(() => setOpenColorDialog(false), 400);

    if (value.sku) {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      onChange && onChange(value.sku);

      return setTimeout(() => {
        dispatch({
          event: 'ga_event',
          eventAction: 'rainbowpicker',
          eventCategory: 'userInteraction',
          eventLabel: 'rainbowpicker',
          eventValue: 'variationChange',
        });
      }, 550);
    }

    return dispatch({
      event: 'ga_event',
      eventAction: 'rainbowpicker',
      eventCategory: 'userInteraction',
      eventLabel: 'rainbowpicker',
      eventValue: 'close',
    });
  };

  const modalCloseHandler = () => {
    dispatch({
      event: 'ga_event',
      eventAction: 'rainbowpicker',
      eventCategory: 'userInteraction',
      eventLabel: 'rainbowpicker',
      eventValue: 'close',
    });
    setOpenColorDialog(false);
  };

  useEffect(() => {
    if (openColorDialog) {
      window.scroll({ behavior: 'smooth', left: 0, top: 0 });
    }
  }, [openColorDialog]);

  const handleColorPickerClick = () => {
    dispatchGTMEvent({
      event: 'ga_event',
      eventAction: 'rainbowpicker',
      eventCategory: 'userInteraction',
      eventLabel: 'rainbowpicker',
      eventValue: 'open',
    });
    setOpenColorDialog(true);
  };

  const dimension = dimensions.find(({ axisType }) => axisType === 'COLOR');

  if (!dimension) {
    return null;
  }

  return (
    <>
      <ImageGalleryCallToActionButton onClick={handleColorPickerClick}>
        <ColorPicker32 />
      </ImageGalleryCallToActionButton>
      {openColorDialog && (
        <Modal open={openColorDialog} onClose={modalCloseHandler}>
          <Slide in={openColorDialog} direction="up">
            <Box
              sx={{
                width: '100%',
                backgroundColor: 'text.light',
                position: 'fixed',
                bottom: 0,
                px: 2,
              }}
            >
              <Stack
                direction="row"
                columnGap={1}
                alignItems="center"
                justifyContent="space-between"
                sx={{
                  my: 2.5,
                }}
              >
                <Typography variant="body2">{formatMessage(messages.selectColor)}</Typography>
                <CloseIconButton onClick={modalCloseHandler} />
              </Stack>
              <Box sx={{ mb: 3 }}>
                <HorizontalScroll
                  gap={1.5}
                  scrollToItemIndex={dimension.values.findIndex(({ isSelected }) => isSelected)}
                >
                  {dimension.values.map((value) => (
                    <ImageGalleryColorDialogItem
                      key={value.variationMainImage ?? value.sku}
                      value={value}
                      onSelect={onSelect}
                    />
                  ))}
                </HorizontalScroll>
              </Box>
            </Box>
          </Slide>
        </Modal>
      )}
    </>
  );
};
